import { FC } from 'react';
import { Content } from '@prismicio/client';
import {
  SliceComponentProps,
  PrismicRichText,
  PrismicLink,
} from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import StarfieldBackground from '@/components/StarfieldBackground';

/**
 * Props for `GetStartedForm`.
 */
export type GetStartedFormProps =
  SliceComponentProps<Content.FeatureCardGridSlice>;

/**
 * Component for "GetStartedForm" Slices.
 */
const GetStartedForm: FC<GetStartedFormProps> = ({ slice }) => {
  const { section_title, cards } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative text-white pt-10 pb-24 md:pt-48 md:pb-40 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0F423A 0%, #0E1010 100%)',
      }}
    >
      <StarfieldBackground />
      <div className="relative z-10 container mx-auto px-4 md:px-6">
        {/* Header Section */}
        <div className="text-center mt-20 mb-16 md:mt-0">
          {section_title && (
            <div className="mb-6">
              <PrismicRichText
                field={section_title}
                components={{
                  heading2: ({ children }) => (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-normal leading-tight font-inter">
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* Values Grid */}
        {cards && cards.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-8">
            {cards.map((card, index) => (
              <div
                key={index}
                className="rounded-2xl p-6 flex flex-col justify-between min-h-[340px] bg-[rgba(0,0,0,0.2)] shadow-[0_4.325px_64.871px_0_rgba(0,0,0,0.08)] backdrop-blur-lg border border-white/10"
              >
                {/* Icon */}
                {card.icon && (
                  <div className="mb-4">
                    <PrismicNextImage
                      field={card.icon}
                      className="w-14 h-14 object-contain"
                      fallbackAlt=""
                    />
                  </div>
                )}

                {/* Overline */}
                {card.overline && (
                  <p className="text-base text-white/80 mb-1 font-normal">
                    {card.overline}
                  </p>
                )}

                {/* Headline */}
                {card.headline && (
                  <div className="mb-4">
                    <PrismicRichText
                      field={card.headline}
                      components={{
                        heading4: ({ children }) => (
                          <h4 className="text-2xl font-bold text-white leading-snug text-left">
                            {children}
                          </h4>
                        ),
                        paragraph: ({ children }) => (
                          <p className="text-lg font-bold text-white leading-snug text-left">
                            {children}
                          </p>
                        ),
                      }}
                    />
                  </div>
                )}

                {/* CTA Button */}
                {card.cta && (
                  <div className="mt-auto">
                    <PrismicLink
                      field={card.cta}
                      className="w-full inline-flex items-center justify-center px-6 py-3 font-bold rounded-full transition-all duration-300 border border-[#eaeaea] bg-[rgba(234,234,234,0.3)] text-white shadow-md hover:rounded-[100px] hover:border-t hover:border-t-white hover:bg-[radial-gradient(55.37%_55.22%_at_50.29%_89.58%,_#05F273_0%,_#03BF9E_100%)] hover:shadow-[0_0_0_3px_rgba(3,194,155,0.20),_0_0_60px_0_#05F36C] group"
                    >
                      <span className="flex items-center gap-2 w-full justify-center">
                        <span className="inline-block -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 22 22"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M6 11H16"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M12 7L16 11L12 15"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <PrismicRichText field={card.button_label} />
                      </span>
                    </PrismicLink>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default GetStartedForm;
